import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, X, AlertTriangle, Info } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type {
  CalendarCollection,
  CalendarEventInput,
  EventInstance,
  RecurrenceRule,
  EventReminder,
} from '../types';
import { REMINDER_PRESETS, PRESET_COLORS } from '../types';
import type { CalendarEventTemplateDetail } from '@/lib/api';
import { RecurrenceBuilder } from './RecurrenceBuilder';
import { ColorPicker } from './ColorPicker';
import { TemplatePicker } from './TemplatePicker';
import {
  parseRruleToRule,
  buildRruleString,
  findConflicts,
  formatTime,
  type BreakWarning,
} from '../utils';

export interface EventDialogSettings {
  defaultCalendarId: string | null;
  defaultEventDuration: number;
  defaultEventReminder: number | null;
  defaultEventStatus: 'confirmed' | 'tentative';
  defaultEventColor: string | null;
  showConflictWarnings: boolean;
  allowDoubleBooking: boolean;
  timeFormat: '12h' | '24h';
  minBreakBetweenEvents: number;
}

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: CalendarEventInput & { rruleObj?: RecurrenceRule | null }) => Promise<void>;
  calendars: CalendarCollection[];
  defaultCalendarId: string | null;
  initialData?: Partial<CalendarEventInput>;
  editEvent?: EventInstance | null;
  title?: string;
  templates?: CalendarEventTemplateDetail[];
  onSaveAsTemplate?: (name: string) => void;
  settings?: EventDialogSettings;
  existingEvents?: EventInstance[];
  /** @deprecated Use settings.defaultEventDuration instead */
  defaultEventDuration?: number;
  /** @deprecated Use settings.defaultEventReminder instead */
  defaultEventReminder?: number | null;
  /** @deprecated Use settings.defaultEventStatus instead */
  defaultEventStatus?: 'confirmed' | 'tentative';
  /** @deprecated Use settings.defaultEventColor instead */
  defaultEventColor?: string | null;
  /** @deprecated Use settings.showConflictWarnings instead */
  showConflictWarnings?: boolean;
  /** @deprecated Use settings.allowDoubleBooking instead */
  allowDoubleBooking?: boolean;
  /** @deprecated Use settings.timeFormat instead */
  timeFormat?: '12h' | '24h';
  /** @deprecated Use settings.minBreakBetweenEvents instead */
  minBreakBetweenEvents?: number;
}

function formatDateForInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatTimeForInput(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export function EventDialog({
  open,
  onClose,
  onSave,
  calendars,
  defaultCalendarId,
  initialData,
  editEvent,
  title: dialogTitle,
  templates,
  onSaveAsTemplate,
  settings: settingsProp,
  existingEvents = [],
  defaultEventDuration: legacyDefaultEventDuration,
  defaultEventReminder: legacyDefaultEventReminder,
  defaultEventStatus: legacyDefaultEventStatus,
  defaultEventColor: legacyDefaultEventColor,
  showConflictWarnings: legacyShowConflictWarnings,
  allowDoubleBooking: legacyAllowDoubleBooking,
  timeFormat: legacyTimeFormat,
  minBreakBetweenEvents: legacyMinBreakBetweenEvents,
}: EventDialogProps) {
  // Resolve settings: prefer the grouped settings object, fall back to individual props
  const defaultEventDuration =
    settingsProp?.defaultEventDuration ?? legacyDefaultEventDuration ?? 60;
  const defaultEventReminder =
    settingsProp?.defaultEventReminder ?? legacyDefaultEventReminder ?? null;
  const defaultEventStatus =
    settingsProp?.defaultEventStatus ?? legacyDefaultEventStatus ?? 'confirmed';
  const defaultEventColor = settingsProp?.defaultEventColor ?? legacyDefaultEventColor ?? null;
  const showConflictWarnings =
    settingsProp?.showConflictWarnings ?? legacyShowConflictWarnings ?? true;
  const allowDoubleBooking = settingsProp?.allowDoubleBooking ?? legacyAllowDoubleBooking ?? true;
  const timeFormat = settingsProp?.timeFormat ?? legacyTimeFormat ?? '12h';
  const minBreakBetweenEvents =
    settingsProp?.minBreakBetweenEvents ?? legacyMinBreakBetweenEvents ?? 0;
  const [saving, setSaving] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('10:00');
  const [isAllDay, setIsAllDay] = useState(false);
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [showColor, setShowColor] = useState(false);
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<'confirmed' | 'tentative' | 'cancelled'>('confirmed');
  const [recurrence, setRecurrence] = useState<RecurrenceRule | null>(null);
  const [reminders, setReminders] = useState<EventReminder[]>([
    { type: 'notification', minutesBefore: 15 },
  ]);
  const [tags, setTags] = useState('');

  // Initialize form only on open transition (not on every rerender while open)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Dialog just opened — initialize form
      if (editEvent) {
        setEventTitle(editEvent.title);
        const start = new Date(editEvent.startAt);
        const end = new Date(editEvent.endAt);
        setStartDate(formatDateForInput(start));
        setStartTime(formatTimeForInput(start));
        setEndDate(formatDateForInput(end));
        setEndTime(formatTimeForInput(end));
        setIsAllDay(editEvent.isAllDay);
        setDescription(editEvent.description || '');
        setLocation(editEvent.location || '');
        setCalendarId(editEvent.calendarId);
        setColor(editEvent.color);
        setShowColor(!!editEvent.color);
        setUrl(editEvent.url || '');
        setStatus(editEvent.status);
        setRecurrence(parseRruleToRule(editEvent.rrule));
        setReminders(editEvent.reminders || []);
        setTags(editEvent.tags?.join(', ') || '');
      } else {
        setEventTitle('');
        const now = new Date();
        const startStr = initialData?.startAt
          ? formatDateForInput(new Date(initialData.startAt))
          : formatDateForInput(now);
        setStartDate(startStr);
        const startDt = initialData?.startAt ? new Date(initialData.startAt) : null;
        setStartTime(startDt ? formatTimeForInput(startDt) : '09:00');

        if (initialData?.endAt) {
          setEndDate(formatDateForInput(new Date(initialData.endAt)));
          setEndTime(formatTimeForInput(new Date(initialData.endAt)));
        } else {
          // Compute end time from start + defaultEventDuration
          const baseStart =
            startDt ||
            (() => {
              const d = new Date(now);
              d.setHours(9, 0, 0, 0);
              return d;
            })();
          const computedEnd = new Date(baseStart.getTime() + defaultEventDuration * 60000);
          setEndDate(formatDateForInput(computedEnd));
          setEndTime(formatTimeForInput(computedEnd));
        }

        setIsAllDay(initialData?.isAllDay || false);
        setDescription('');
        setLocation('');
        setCalendarId(defaultCalendarId || calendars[0]?.id || '');
        setColor(defaultEventColor);
        setShowColor(!!defaultEventColor);
        setUrl('');
        setStatus(defaultEventStatus);
        setRecurrence(null);
        setReminders(
          defaultEventReminder != null
            ? [{ type: 'notification', minutesBefore: defaultEventReminder }]
            : [{ type: 'notification', minutesBefore: 15 }],
        );
        setTags('');
      }
    }
    prevOpenRef.current = open;
  }, [
    open,
    editEvent,
    initialData,
    defaultCalendarId,
    calendars,
    defaultEventDuration,
    defaultEventReminder,
    defaultEventStatus,
    defaultEventColor,
  ]);

  const handleSave = async () => {
    if (!eventTitle.trim()) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    if (!calendarId) {
      toast({ title: 'Calendar required', variant: 'destructive' });
      return;
    }

    const startAt = isAllDay ? `${startDate}T00:00:00.000Z` : `${startDate}T${startTime}:00`;
    const endAt = isAllDay ? `${endDate}T23:59:59.999Z` : `${endDate}T${endTime}:00`;

    if (new Date(endAt) < new Date(startAt)) {
      toast({ title: 'End must be after start', variant: 'destructive' });
      return;
    }

    const input: CalendarEventInput & { rruleObj?: RecurrenceRule | null } = {
      calendarId,
      title: eventTitle.trim(),
      description: description || undefined,
      location: location || undefined,
      startAt,
      endAt,
      isAllDay,
      status,
      color: showColor ? color : null,
      url: url || undefined,
      isRecurring: !!recurrence,
      rrule: recurrence ? buildRruleString(recurrence) : null,
      reminders,
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      rruleObj: recurrence,
    };

    setSaving(true);
    try {
      await onSave(input);
      onClose();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save event',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const addReminder = () => {
    setReminders((prev) => [...prev, { type: 'notification', minutesBefore: 15 }]);
  };

  const removeReminder = (index: number) => {
    setReminders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelectTemplate = (template: CalendarEventTemplateDetail) => {
    setEventTitle(template.title);
    setDescription(template.description || '');
    setLocation(template.location || '');
    setIsAllDay(template.isAllDay);
    setStatus(template.status);
    setColor(template.color);
    setShowColor(!!template.color);
    setUrl(template.url || '');
    setRecurrence(parseRruleToRule(template.rrule));
    setReminders(template.reminders || [{ type: 'notification', minutesBefore: 15 }]);
    setTags(template.tags?.join(', ') || '');
    if (template.calendarId) setCalendarId(template.calendarId);

    // Set duration-based end time from start
    if (!isAllDay && template.durationMinutes) {
      const start = new Date(`${startDate}T${startTime}:00`);
      const end = new Date(start.getTime() + template.durationMinutes * 60000);
      setEndDate(formatDateForInput(end));
      setEndTime(formatTimeForInput(end));
    }
  };

  // Conflict detection
  const conflicts = useMemo(() => {
    if (!showConflictWarnings || isAllDay || !startDate || !startTime || !endDate || !endTime) {
      return [];
    }
    const startAt = `${startDate}T${startTime}:00`;
    const endAt = `${endDate}T${endTime}:00`;
    if (new Date(endAt) <= new Date(startAt)) return [];
    return findConflicts(startAt, endAt, existingEvents, editEvent?.eventId);
  }, [
    showConflictWarnings,
    isAllDay,
    startDate,
    startTime,
    endDate,
    endTime,
    existingEvents,
    editEvent,
  ]);

  // Break gap warning
  const breakWarnings = useMemo<BreakWarning[]>(() => {
    if (
      minBreakBetweenEvents <= 0 ||
      isAllDay ||
      !startDate ||
      !startTime ||
      !endDate ||
      !endTime
    ) {
      return [];
    }
    const newStart = new Date(`${startDate}T${startTime}:00`);
    const newEnd = new Date(`${endDate}T${endTime}:00`);
    if (newEnd <= newStart) return [];
    const minGapMs = minBreakBetweenEvents * 60 * 1000;
    const warnings: BreakWarning[] = [];
    for (const e of existingEvents) {
      if (e.isAllDay) continue;
      if (editEvent && e.eventId === editEvent.eventId) continue;
      const eStart = new Date(e.startAt);
      const eEnd = new Date(e.endAt);
      // Check if existing event ends just before the new event starts
      const gapBefore = newStart.getTime() - eEnd.getTime();
      if (gapBefore > 0 && gapBefore < minGapMs) {
        warnings.push({
          eventBefore: e,
          eventAfter: e /* placeholder */,
          gapMinutes: Math.round(gapBefore / 60000),
        });
      }
      // Check if existing event starts just after the new event ends
      const gapAfter = eStart.getTime() - newEnd.getTime();
      if (gapAfter > 0 && gapAfter < minGapMs) {
        warnings.push({ eventBefore: e, eventAfter: e, gapMinutes: Math.round(gapAfter / 60000) });
      }
    }
    return warnings;
  }, [
    minBreakBetweenEvents,
    isAllDay,
    startDate,
    startTime,
    endDate,
    endTime,
    existingEvents,
    editEvent,
  ]);

  const saveBlocked = !allowDoubleBooking && conflicts.length > 0;

  const [templateNamePrompt, setTemplateNamePrompt] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const handleSaveAsTemplate = () => {
    if (!templateName.trim()) return;
    onSaveAsTemplate?.(templateName.trim());
    setTemplateNamePrompt(false);
    setTemplateName('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle || (editEvent ? 'Edit Event' : 'New Event')}</DialogTitle>
          <DialogDescription>
            {editEvent ? 'Update the event details.' : 'Create a new calendar event.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Template picker (create mode only) */}
          {!editEvent && templates && templates.length > 0 && (
            <TemplatePicker templates={templates} onSelect={handleSelectTemplate} />
          )}

          {/* Title */}
          <div>
            <Label>Title</Label>
            <Input
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              placeholder="Event title"
              autoFocus
            />
          </div>

          {/* All Day */}
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={isAllDay} onCheckedChange={(c) => setIsAllDay(c === true)} />
            <span className="text-sm">All day</span>
          </label>

          {/* Start */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Start date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-sm"
              />
            </div>
            {!isAllDay && (
              <div>
                <Label className="text-xs">Start time</Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          {/* End */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">End date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-sm"
              />
            </div>
            {!isAllDay && (
              <div>
                <Label className="text-xs">End time</Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          {/* Conflict warning */}
          {conflicts.length > 0 && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  <p className="font-medium">
                    {conflicts.length === 1
                      ? 'Scheduling conflict'
                      : `${conflicts.length} scheduling conflicts`}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {conflicts.map((c) => (
                      <li key={c.id} className="text-xs">
                        {c.title} ({formatTime(new Date(c.startAt), timeFormat)} –{' '}
                        {formatTime(new Date(c.endAt), timeFormat)})
                      </li>
                    ))}
                  </ul>
                  {saveBlocked && (
                    <p className="text-xs mt-1 font-medium">
                      Double booking is disabled. Adjust the time to save.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Break gap warning */}
          {breakWarnings.length > 0 && (
            <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  {breakWarnings.map((w, i) => (
                    <p key={i} className="text-xs">
                      Only {w.gapMinutes}min between this and {w.eventBefore.title}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <Label className="text-xs">Location</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Add location"
              className="text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add description"
              rows={3}
              className="text-sm"
            />
          </div>

          {/* Calendar */}
          <div>
            <Label className="text-xs">Calendar</Label>
            <Select value={calendarId} onValueChange={setCalendarId}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select calendar" />
              </SelectTrigger>
              <SelectContent>
                {calendars.map((cal) => (
                  <SelectItem key={cal.id} value={cal.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ backgroundColor: cal.color }}
                      />
                      {cal.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="tentative">Tentative</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recurrence */}
          <RecurrenceBuilder value={recurrence} onChange={setRecurrence} />

          {/* Reminders */}
          <div>
            <Label className="text-xs">Reminders</Label>
            <div className="space-y-2 mt-1">
              {reminders.map((rem, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select
                    value={String(rem.minutesBefore)}
                    onValueChange={(v) => {
                      const next = [...reminders];
                      next[i] = { ...next[i], minutesBefore: parseInt(v, 10) };
                      setReminders(next);
                    }}
                  >
                    <SelectTrigger className="text-xs h-8 flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REMINDER_PRESETS.map((p) => (
                        <SelectItem key={p.minutes} value={String(p.minutes)}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => removeReminder(i)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addReminder} className="text-xs">
                Add reminder
              </Button>
            </div>
          </div>

          {/* Color override */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <Checkbox
                checked={showColor}
                onCheckedChange={(c) => {
                  setShowColor(c === true);
                  if (!c) setColor(null);
                }}
              />
              <span className="text-xs">Custom color</span>
            </label>
            {showColor && <ColorPicker value={color || PRESET_COLORS[0]} onChange={setColor} />}
          </div>

          {/* URL */}
          <div>
            <Label className="text-xs">URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="text-sm"
            />
          </div>

          {/* Tags */}
          <div>
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="meeting, client, ..."
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {onSaveAsTemplate && !templateNamePrompt && (
            <Button
              variant="secondary"
              size="sm"
              className="mr-auto text-xs"
              onClick={() => setTemplateNamePrompt(true)}
              disabled={saving || !eventTitle.trim()}
            >
              Save as Template
            </Button>
          )}
          {templateNamePrompt && (
            <div className="mr-auto flex items-center gap-1">
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name"
                className="h-7 text-xs w-36"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveAsTemplate();
                  if (e.key === 'Escape') setTemplateNamePrompt(false);
                }}
              />
              <Button size="sm" className="h-7 text-xs" onClick={handleSaveAsTemplate}>
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setTemplateNamePrompt(false)}
              >
                Cancel
              </Button>
            </div>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || saveBlocked}
            title={saveBlocked ? 'Resolve scheduling conflicts before saving' : undefined}
          >
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {editEvent ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
